<template>
  <div>
    <b-taglist id="highlight-simulation">
      <b-tag
        type="is-light is-rounded"
        size="is-medium">
        Crédito imóbiliario: <span
          class="has-text-primary"
          data-cy="highlight-credito-imobiliario">{{ formatNumberByCountry(financingAmount) }}</span>
      </b-tag>
      <b-tag
        type="is-light is-rounded"
        size="is-medium">
        Primeira parcela de: <span
          class="has-text-primary"
          data-cy="highlight-primera-parcela">{{ formatNumberByCountry(creditFee) }} + IPCA</span>
      </b-tag>
      <span
        class="button tag is-light is-rounded is-medium"
        data-cy="highlight-data"
        @click="showModal = true">
        +
      </span>
    </b-taglist>
    <b-modal
      v-model="showModal"
      :destroy-on-hide="false"
      aria-role="dialog"
      aria-label="Simulation results"
      close-button-aria-label="Cerrar"
      :width="800"
      class="modal-template">
      <div class="modal-card">
        <header class="modal-card-head background-top-modal box">
          <p class="title pl-4 has-text-white">Sua simulação</p>
        </header>
        <section class="modal-card-body">
          <div class="columns custom-columns">
            <div class="column is-three-fifths result-content">
              <div class="card p-5">
                <div class="pt-5 pl-5 pr-5 pb-2">
                  <p class="title is-size-6 is-size-7-mobile divider">
                    Valor solicitado:
                    <span
                      id="amountRequestedByClient"
                      class="is-pulled-right has-text-weight-light has-text-primary">
                      {{ formatNumberByCountry(amountRequestedByClient || 0, true, 0) }}
                    </span>
                  </p>
                  <p class="title is-size-6 is-size-7-mobile divider">
                    Última parcela:
                    <span
                      id="monthlyTotal"
                      class="is-pulled-right has-text-weight-light has-text-primary">
                      {{ formatNumberByCountry(monthlyTotal, true, 0) }} + IPCA
                    </span>
                  </p>
                  <p class="title is-size-6 is-size-7-mobile divider">
                    CET:
                    <span
                      id="annualPercentageRate"
                      class="is-pulled-right has-text-weight-light has-text-primary">
                      {{ formatNumberByCountry(annualPercentageRate, false, 2) }} % + IPCA
                    </span>
                  </p>
                  <p class="title is-size-6 is-size-7-mobile divider">
                    Taxa de juros mensal:
                    <span
                      id="offer-response-amount-requested-by-client"
                      class="is-pulled-right has-text-weight-light has-text-primary">
                      {{ formatNumberByCountry(monthlyInterestRate, false, 2) }} % + IPCA
                    </span>
                  </p>
                  <p class="title is-size-6 is-size-7-mobile divider">
                    Tipo de amortização:
                    <span
                      id="offer-response-amount-requested-by-client"
                      class="is-pulled-right has-text-weight-light has-text-primary">
                      {{ (amortizationMethod === 'GERMAN') ? 'SAC' : 'PRICE' }}
                    </span>
                  </p>
                  <p class="title is-size-6 is-size-7-mobile divider">
                    Prazo:
                    <span
                      id="offer-response-amount-requested-by-client"
                      class="is-pulled-right has-text-weight-light has-text-primary">
                      {{ numberOfMonths }} meses
                    </span>
                  </p>
                </div>
              </div>
            </div>
            <div class="column">
              <div class="has-text-centered">
                <div class="dest is-primary">
                  <small class="is-size-7 is-size-7-mobile divider">
                    Valor financiado:
                  </small>
                  <span
                    id="offer-response-financing-amount"
                    class="is-size-3 is-block has-text-weight-light has-text-primary"
                    style="line-height: 1">
                    {{ formatNumberByCountry(financingAmount) }}
                  </span>
                  <p class="is-size-7 is-size-7-mobile">
                    Primeira parcela:
                    <span
                      id="offer-response-credit-fee"
                      class="has-text-weight-light has-text-primary">
                      {{ formatNumberByCountry(creditFee) }}
                      <span v-if="!!ipcaValue">{{ $t("ipca") }}</span>
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </b-modal>
  </div>
</template>

<script>

import { formatNumberByCountry } from '@/infrastructure/format';
import { getLatestSimulation } from '@/application/services/api-clients/simulations';

export default {
  name: 'HighlightSimulation',
  props: {
    prospectId: {
      type: String
    }
  },
  data() {
    return {
      financingAmount: 0,
      creditFee: 0,
      ipcaValue: 0,
      amountRequestedByClient: 0,
      monthlyTotal: 0,
      annualPercentageRate: 0,
      monthlyInterestRate: 0,
      amortizationMethod: '',
      numberOfMonths: 0,
      showModal: false
    };
  },
  async mounted() {
    await this.loadHighlightSimulation();
  },
  methods: {
    async loadHighlightSimulation() {
      try {
        const [simulation] = await getLatestSimulation(this.prospectId);
        if (simulation) {
          const { offerResponse, amortizationSchedule, offerParams, offerInputs } = simulation;
          const { financingAmount, creditFee, amountRequestedByClient } = offerResponse;
          const { lastPayment } = amortizationSchedule;
          this.financingAmount = financingAmount;
          this.creditFee = creditFee;
          this.ipcaValue = simulation.ipcaValue;
          this.amountRequestedByClient = amountRequestedByClient;
          this.monthlyTotal = lastPayment.monthlyTotal;
          this.annualPercentageRate = amortizationSchedule.annualPercentageRate * 100;
          this.monthlyInterestRate = offerResponse.monthlyInterestRate * 100;
          this.amortizationMethod = offerParams.amortizationMethod;
          this.numberOfMonths = offerInputs.numberOfMonths;
        }
      } catch (_error) {
        this.$buefy.toast.open({
          position: 'is-bottom-left',
          message: 'Hubo un problema al conectarnos con el servidor',
          type: 'is-danger',
          duration: 5000
        });
      }
    },
    formatNumberByCountry(number, usePrefix, fractionDigits) {
      return formatNumberByCountry({ number, countryIn: 'br', usePrefix, fractionDigits });
    }
  }
};
</script>

<style scoped>
.modal-template {
  justify-content: flex-start;
  padding: 130px 30px 0px 30px;
}
.modal-card-body{
  overflow: initial;
  background: #f6f6f6;
}
.card {
  border-radius: 1rem;
}

.divider {
  border-bottom: 1px solid #f3f3f3;
  padding-bottom: 5px;
}
.dest.is-primary{
  border: 1px solid #00CCD1;
  padding: 0.4rem 0;
  border-radius: 15px;
  margin-bottom: 1.5rem;
  margin-top: 1rem;
  background: #FFF;
}
.divider {
  padding-bottom: 0.4rem;
  margin-bottom: 0.5rem;
}
.dest .divider {
  border-color: #EEE;
  padding-bottom: 0;
}
.background-top-modal {
  background-position: center center;
  background-size: 290%;
  background-repeat: no-repeat;
  background-image: url(../../assets/img/bubbles.svg);
  padding: 2rem !important;
  margin-bottom: 0;
  height: 150px;
  border-radius: 1rem 1rem 0 0 !important;
}

@media screen and (max-width: 768px) {
  .dest.is-primary{
    margin-top: 0.5rem;
    margin-bottom: -70px;
    position: relative;
    top: -70px;
  }

  .custom-columns {
    flex-direction: column-reverse;
    display: flex;
  }
}
@media screen and (min-width: 769px) {
  .modal-content, .modal-card {
    width: auto;
    max-width: 100%;
  }

  .result-content {
    position: relative;
    top: -70px;
    margin-bottom: -70px;
  }
}
@media (max-width: 1248px) {
  .background-top-modal {
    border-top-left-radius: 0 !important;
    border-top-right-radius: 0 !important;
    padding-top: 2.2rem !important;
  }
}
</style>
